import * as React from 'react';
import styled from 'styled-components';
import * as ConnectIDModule from '@connectid-tools/idp-selector-react';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router';
import { useField } from 'formik';
import SecondaryButton from '../../appComponents/common/buttons/SecondaryButton';
import { connectIDParticipantsUrl } from '../../config.local';
import { addCloudCheckActivityToWorkflow, getConnectIdAuthUrl, partialUpdateWorkflow } from '../../core/api';
import { sessionKeys } from '../../core/session';
import { useAsyncAction } from '../Hooks';
import MessageBox from '../../appComponents/common/MessageBox';
import Workflow from '../../models/be/Workflow';
import { getUniqueQueryString, hasValue, single } from '../../lib/utils';
import { Component } from '../../models/be/db/FormSpecification';
import { ccidv } from '../../core/constants';

const IdVerificationTypeSelection = (props: Props) => {
	const { workflow, component } = props;
	const location = useLocation();
	const fieldName = single(component.fieldNames);
	const formikProps = { name: fieldName };
	const [, , helpers] = useField(formikProps);
	const activityId = getUniqueQueryString(location.search, 'activityId');
	const [, errorMessage, performAsyncAction] = useAsyncAction(false);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [error, setError] = React.useState<string>(null);
	const [selectedIdVerificationType, setSelectedIdVerificationType] = React.useState<string>(
		workflow?.customFields?.idVerificationType as string
	);
	const [loadingMessage, setLoadingMessage] = React.useState<string>(null);
	const { CidButton } = ConnectIDModule;

	const setNavigationButtonsDisabled = (value: boolean) => {
		if (props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(value);
		if (props.setBackButtonsDisabled) props.setBackButtonsDisabled(value);
	};

	React.useEffect(() => {
		setNavigationButtonsDisabled(!(hasValue(selectedIdVerificationType) && selectedIdVerificationType === ccidv));
	}, []);

	const updateIdVerificationTypeInWorkflow = async (idvType: string) => {
		performAsyncAction(async () => {
			const partialObject: Record<string, unknown> = {
				clientCustomerId: workflow.clientCustomerId
			};
			partialObject.customFields = { ...workflow.customFields, idVerificationType: idvType };
			await partialUpdateWorkflow(workflow.id, activityId, partialObject);
			helpers.setValue(idvType);
			setSelectedIdVerificationType(idvType);
		});
	};

	const verifyWithCloudcheck = async () => {
		await performAsyncAction(async () => {
			setLoading(true);
			setLoadingMessage('Redirecting to Cloud check');
			setNavigationButtonsDisabled(true);
			await addCloudCheckActivityToWorkflow(workflow.id);
			await updateIdVerificationTypeInWorkflow(ccidv);
			window.location.reload();
		});
	};

	if (errorMessage || error) return <MessageBox error={errorMessage || error} />;
	if (loading) return <div>{loadingMessage}</div>;

	function delay(ms: number): Promise<void> {
		return new Promise((resolve) => setTimeout(resolve, ms));
	}

	return (
		<>
			{selectedIdVerificationType !== ccidv && (
				<>
					<StyledText>Please choose one of these methods to verify your identity.</StyledText>
					<StyledMethodSection>
						<StyledSectionTitle>ConnectID</StyledSectionTitle>
						<StyledCidButton>
							<CidButton
								onProceed={async (authorisationServerClicked: string) => {
									setLoading(true);
									setLoadingMessage('Redirecting to ConnectID');
									setNavigationButtonsDisabled(true);
									await performAsyncAction(async () => {
										await updateIdVerificationTypeInWorkflow('CIDV');
										try {
											const response = await getConnectIdAuthUrl(authorisationServerClicked, workflow.id);
											localStorage.setItem(sessionKeys.workflowId, workflow.id);
											localStorage.setItem(sessionKeys.ConnectIdNonce, response.nonce);
											localStorage.setItem(sessionKeys.ConnectIdState, response.state);
											localStorage.setItem(sessionKeys.ConnectIdCodeVerifier, response.codeVerifier);
											localStorage.setItem(sessionKeys.ConnectIdAuthorisationServerId, response.authorisationServerId);

											document.location.href = response.authUrl;
											await delay(1000);
										} catch (ex) {
											setError('There is currently an issue with ConnectID, please go back and use Cloudcheck instead');
											if (props.setSubmitButtonsDisabled) props.setSubmitButtonsDisabled(true);
											if (props.setBackButtonsDisabled) props.setBackButtonsDisabled(false);
											setSelectedIdVerificationType(null);
										}
										setLoading(false);
									});
								}}
								rpLogoUrl={`${window.location.origin}/images/logo.v-3d006d16cc96d78b9b800912af7edbe0.png`}
								rpDataSharingDescription="Your details will be shared for the purpose of verifying your identity with VETTING.com."
								claims={['given_name', 'middle_name', 'family_name', 'birthdate']}
								participantsUri={connectIDParticipantsUrl}
								onError={(connectIdError: string) => {
									Sentry.captureMessage(`connectIdError: ${connectIdError}`, 'error');
									setError(connectIdError);
								}}
								type="medium process"
								introTextType="verify-trust"
								showInfoContent
							/>
						</StyledCidButton>
					</StyledMethodSection>
				</>
			)}
			<StyledMethodSection>
				<StyledSectionTitle>Cloudcheck</StyledSectionTitle>
				<StyledText>
					Verify your identity through Cloudcheck with your Driver&apos;s Licence or Australian Passport.
				</StyledText>
				<StyledButtons>
					<StyledSecondaryButton
						disabled={selectedIdVerificationType === ccidv}
						onClick={verifyWithCloudcheck}
						text="Verify with Cloudcheck"
					/>
				</StyledButtons>
			</StyledMethodSection>
		</>
	);
};

export default IdVerificationTypeSelection;

interface Props {
	component: Component;
	workflow: Workflow;
	setBackButtonsDisabled: (disabled: boolean) => void;
	setSubmitButtonsDisabled: (disabled: boolean) => void;
}

const StyledMethodSection = styled.section`
	border: 1px solid #d1d1d1;
	padding: 20px;
	border-radius: 10px;
	margin-bottom: 20px;
	background-color: #ffff;
`;

const StyledSectionTitle = styled.p`
	font-size: 20px;
	font-weight: bold;
	margin-top: -10px;
	margin-bottom: 15px;
	font-weight: 100;
`;

const StyledButtons = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const StyledSecondaryButton = styled(SecondaryButton)`
	background-color: #262626;
	color: white;
	padding: 15px;
	font-size: 14px;
	border-radius: 12px;
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 48px;
	margin-top: 15px;
	font-family: 'DM Sans', sans-serif;
	font-weight: 400;
`;

const StyledText = styled.div`
	font-size: 14px;
	margin-bottom: 20px;
	font-weight: normal;
`;

const StyledCidButton = styled.div`
	margin-bottom: 20px;
	width: 100%;
	display: flex;
	justify-content: center;
	flex-direction: column;
	button {
		width: 100%;
	}
`;
